import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface NavItem {
  label: string;
  href: string;
}

const Navigation: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const { theme, toggleTheme } = useTheme();

  const navItems: NavItem[] = [
    { label: 'Home', href: '#home' },
    { label: 'Services', href: '#services' },
    { label: 'Portfolio', href: '#portfolio' },
    { label: 'About', href: '#about' },
    { label: 'Process', href: '#process' },
    { label: 'Testimonials', href: '#testimonials' },
    { label: 'Contact', href: '#contact' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      // Update active section based on scroll position
      const sections = navItems.map(item => item.href.substring(1));
      const currentSection = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [navItems]);

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
    {/* <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-100 overflow-hidden ${...}`}> */}
    {/* <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full"> */}
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-100 overflow-hidden ${
        isScrolled
          ? 'glass backdrop-blur-md bg-white/80 dark:bg-navy/80 border-b border-gray-300 dark:border-gray-900'
          : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-2">
          <div className="flex items-center flex-shrink-0 justify-between">
            {/* Logo */}
            <div className="items-center">
              <button
                onClick={() => scrollToSection('#home')}
                className="text-3xl font-bold transition-colors duration-300 flex items-center"
                >
                {theme === 'light' ? (
                  <img src={`${process.env.PUBLIC_URL}/assets/home/jmc-logo-light.png`} alt="Jagathi Solutions" className="h-11 w-auto object-contain" />
                ) : (
                  <img src={`${process.env.PUBLIC_URL}/assets/home/jmc-logo-dark.png`} alt="Jagathi Solutions" className="h-11 w-auto object-contain" />
                )}
              </button>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:block">
              <div className="flex items-center space-x-8">
                {navItems.map((item) => (
                  <button
                    key={item.label}
                    onClick={() => scrollToSection(item.href)}
                    className={`px-3 py-2 text-sm font-medium transition-all duration-300 relative group ${
                      activeSection === item.href.substring(1)
                        ? theme === 'dark' ? 'text-teal-300' : 'text-teal-light'
                        : theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {item.label}
                    <span className={`absolute bottom-0 left-0 w-full h-0.5 gradient-primary transition-all duration-300 ${
                      activeSection === item.href.substring(1) ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'
                    }`}></span>
                  </button>
                ))}
              </div>
            </div>

            {/* Theme Toggle Button */}
            <div className="hidden lg:block">
              <button
                onClick={toggleTheme}
                className="p-3 glass rounded-full dark:text-gray-300 text-gray-600 hover:dark:text-white hover:text-gray-900 hover:bg-opacity-30 transition-all duration-100 transform hover:scale-105"
                aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
              >
                {theme === 'light' ? (
                  // Moon icon for dark mode
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                ) : (
                  // Sun icon for light mode
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                )}
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <button
                onClick={toggleMobileMenu}
                className="p-2 glass rounded-lg dark:text-gray-300 text-gray-600 hover:dark:text-white hover:text-gray-900 hover:bg-opacity-30 transition-all duration-300"
                aria-label="Toggle mobile menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <div className={`lg:hidden transition-all duration-300 ${
          isMobileMenuOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'
        } overflow-hidden`}>
          {/* <div className="glass border-t border-gray-300 dark:border-gray-900 overflow-y-auto max-h-[80vh]"> */}
          <div className="glass border-t border-gray-300 dark:border-gray-900 overflow-y-auto max-h-[80vh]">
            <div className="px-4 py-4 space-y-3 pb-6">
              {navItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => {
                    scrollToSection(item.href);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`block w-full text-left px-3 py-2.5 text-sm font-medium transition-all duration-300 rounded-lg ${
                    activeSection === item.href.substring(1)
                      ? theme === 'dark'
                        ? 'text-teal-200 bg-teal-900/20'
                        : 'text-teal-600 bg-teal-100'
                      : theme === 'dark'
                        ? 'text-gray-300 hover:text-white hover:bg-white/5'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {item.label}
                </button>
              ))}

              {/* Mobile Theme Toggle Button */}
              <div className="pt-3 border-t border-gray-300 dark:border-gray-900">
                <button
                  onClick={toggleTheme}
                  className="w-full px-4 py-3 glass rounded-lg dark:text-gray-300 text-gray-600 hover:dark:text-white hover:text-gray-900 hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center space-x-2 text-sm"
                  aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
                >
                  {theme === 'light' ? (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                      </svg>
                      <span>Dark Mode</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      <span>Light Mode</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 dark:bg-gray-900 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Custom Cursor */}
      {/* <div className="custom-cursor">
        <div className="cursor-dot"></div>
        <div className="cursor-outline"></div>
      </div> */}
    </>
  );
};

export default Navigation;
